import { Formik, Form, Field, ErrorMessage } from "formik";
import Button from "../../button/button.component";
import {
  CMHeadingContainer,
  CMBox,
  CMOffer,
  CMOrderDetails,
} from "./order-preview.styles";
import Image, { IMAGE_TYPE } from "../../image/image.component";
import Dropdown from "../../../components/dropdownOrder/dropdownOrder.component";
import * as Yup from "yup";
import {
  countDecimal,
  expoToString,
  formatCommaNumbers,
  getTotalValue,
  isNumber,
  isNumberWithoutDot,
  toFix,
  truncateToDecimals,
} from "../../../Utils/common.utils";
import { useRef, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import "./OrderPreview.scss";
import { getOrderbookUnits } from "../../../Services/prod/auth.service";
import { toast } from "react-toastify";

const OrderPreview = ({
  handleNext,
  auction,
  editValues,
  pairBalance,
  pairFees,
}) => {
  const formRef = useRef();
  const priceDecimalLimit = 2;

  const options = [
    { value: "Buy", description: "Buy", text: "Buy" },
    { value: "Sell", description: "Sell", text: "Sell" },
  ];

  const priceOptions = [
    { value: "Market", description: "Market", text: "Market" },
    { value: "Stop Limit", description: "Stop Limit", text: "Stop Limit" },
    { value: "Limit", description: "Limit", text: "Limit" },
  ];

  const validationSchema = Yup.object({
    selectedOption: Yup.string().required("Please select an option"),
    selectedPriceOption: Yup.string().required("Please select a price type"),
    price: Yup.number("Price must be a number").when("selectedPriceOption", {
      is: (value) => value === "Limit" || value === "Stop Limit",
      then: Yup.number()
        .required("Price can't be empty")
        .test("isPositive", "Enter a valid price", (value) => value > 0)
        .test("Is less than 3 decimal places?", "", function (value) {
          return value
            ? countDecimal(expoToString(Number(value))) <=
                Number(priceDecimalLimit)
            : true;
        })
        .test("enough balance", "You don't have enough balance", function () {
          const { selectedOption } = this.parent;
          if (selectedOption === "Buy") {
            return (
              getTotalValue(this.parent.price, this.parent.quantity || 0) <=
              Number(toFix(pairBalance?.USD, 2))
            );
          }
          return true;
        }),
      otherwise: Yup.number().notRequired(),
    }),
    quantity: Yup.string()
      .required("Quantity can't be empty")
      .test("isPositive", "Enter a valid amount", (value) => value > 0)
      .test("isPriceFilled", "Enter the price first", function (value) {
        const { selectedPriceOption, price } = this.parent;
        if (
          selectedPriceOption === "Limit" ||
          selectedPriceOption === "Stop Limit"
        ) {
          return price !== "";
        }
        return true;
      })
      .test("Is less than 3 decimal places?", "", (value) =>
        value
          ? countDecimal(expoToString(Number(value))) <=
            Number(priceDecimalLimit)
          : true
      )
      .test(
        "enough balance ",
        "You don't have enough balance",
        function (value) {
          const { selectedOption } = this.parent;
          if (selectedOption === "Sell") {
            return (
              Number(value) <=
              Number(toFix(pairBalance[auction?.symbol?.toUpperCase()], 2))
            );
          }
          return true;
        }
      ),
    stop: Yup.number().when("selectedPriceOption", {
      is: "Stop Limit",
      then: Yup.number()
        .required("Stop can't be empty")
        .test(
          "isPositive",
          "The number must be greater than 0",
          (value) => value > 0
        ),
      otherwise: Yup.number().notRequired(),
    }),
  });

  //********check units from orderBook********//
  const getOrderbookUnitsFn = async (values) => {
    try {
      let data = {
        pairName: auction?.symbol?.toLowerCase() + "_usd",
        limit: 50,
      };
      const resPairFees = await getOrderbookUnits(data);
      const { buyOrderBook, sellOrderBook } = resPairFees?.data || {};
      const quantityToCheck = Number(values?.quantity);

      if (values?.selectedOption === "Buy") {
        if (!sellOrderBook || sellOrderBook.length === 0) {
          toast.error("Currently, there are no active market orders.", {
            autoClose: 10000, // display for 5 seconds (default is 5000ms)
            position: "top-center", // or "bottom-center" as per your preference
          });
          return false;
        }
        const totalSellQuantity = sellOrderBook.reduce(
          (sum, order) => sum + order.quantity,
          0
        );
        if (quantityToCheck > totalSellQuantity) {
          toast.error("Currently, there are no active market orders.", {
            autoClose: 10000, // display for 5 seconds (default is 5000ms)
            position: "top-center", // or "bottom-center" as per your preference
          });
          return false;
        }
      } else if (values?.selectedOption === "Sell") {
        if (!buyOrderBook || buyOrderBook.length === 0) {
          toast.error("Currently, there are no active market orders.", {
            autoClose: 10000, // display for 5 seconds (default is 5000ms)
            position: "top-center", // or "bottom-center" as per your preference
          });
          return false;
        }
        const totalBuyQuantity = buyOrderBook.reduce(
          (sum, order) => sum + order.quantity,
          0
        );
        if (quantityToCheck > totalBuyQuantity) {
          toast.error("Currently, there are no active market orders.", {
            autoClose: 10000, // display for 5 seconds (default is 5000ms)
            position: "top-center", // or "bottom-center" as per your preference
          });
          return false;
        }
      }

      return true; // Order book checks passed
    } catch (error) {
      toast.error("Failed to fetch data.");
      return false;
    }
  };
  return (
    <>
      <div className="order-preview">
        <Formik
          innerRef={formRef}
          initialValues={{
            selectedOption: editValues?.selectedOption || "Buy",
            price: editValues?.price || "",
            selectedPriceOption: editValues?.selectedPriceOption || "Limit",
            quantity: editValues?.quantity || "",
            stop: editValues?.stop || "",
          }}
          validationSchema={validationSchema}
          onSubmit={async (values) => {
            if (values.selectedPriceOption === "Market") {
              const orderbookValid = await getOrderbookUnitsFn(values);
              if (orderbookValid) {
                handleNext(values); // Proceed to the next step if order book checks pass
              }
            } else {
              handleNext(values); // Proceed for non-market orders
            }
          }}
        >
          {({ values, setFieldValue }) => (
            <Form className="order-form">
              <CMHeadingContainer>
                <h3>
                  Trading Symbol / <span>{auction?.symbol}</span>
                </h3>
              </CMHeadingContainer>

              <CMOrderDetails className="d-flex align-items-center pt-2">
                <div className="image-column align-items-center">
                  <Image
                    src={
                      auction.imgSrc && auction.imgSrc.length > 0
                        ? auction.imgSrc[0]
                        : ""
                    }
                    imageType={IMAGE_TYPE.rounded}
                    alt={auction.title}
                    width={"100%"}
                    height={"auto"}
                  />
                </div>
                <div className="text-column">
                  <h5>{auction.symbolType}</h5>
                  <h1>{auction.assetName}</h1>
                </div>
              </CMOrderDetails>

              <CMBox className="orderField">
                <span>
                  Available -{" "}
                  {values.selectedOption === "Buy"
                    ? formatCommaNumbers(toFix(pairBalance?.USD, 2))
                    : formatCommaNumbers(
                        toFix(pairBalance[auction?.symbol?.toUpperCase()], 2)
                      )}
                </span>{" "}
                <span style={{ fontWeight: "bold" }}>
                  {values.selectedOption === "Buy"
                    ? "USD"
                    : auction?.symbol?.toUpperCase()}
                </span>
                <Row>
                  <Col lg={6}>
                    <div className="units-column greenInput">
                      <div className="label-column">
                        <label htmlFor="priceType">Side</label>
                      </div>
                      <div className="combined-input-container d-flex">
                        <input
                          type="text"
                          className="text-input"
                          style={{
                            backgroundColor:
                              values.selectedOption === "Buy"
                                ? "#429800"
                                : "red",
                          }}
                          disabled
                        />

                        <Field name="selectedOption">
                          {({ field }) => (
                            <Dropdown
                              {...field}
                              options={options}
                              value={values?.selectedOption}
                              isFormikControl={false}
                              onChange={(e) =>
                                setFieldValue("selectedOption", e.target.value)
                              }
                              className="Dropd"
                            />
                          )}
                        </Field>
                        <ErrorMessage
                          name="selectedOption"
                          component="div"
                          className="error"
                        />
                      </div>
                    </div>
                  </Col>
                  <Col lg={6}>
                    <div className="units-column">
                      <div className="label-column">
                        <label htmlFor="priceType">Type</label>
                      </div>
                      <div className="input-column">
                        <Field name="selectedPriceOption">
                          {({ field }) => (
                            <Dropdown
                              {...field}
                              options={priceOptions}
                              value={values.selectedPriceOption}
                              isFormikControl={false}
                              onChange={(e) => {
                                setFieldValue(
                                  "selectedPriceOption",
                                  e.target.value
                                );
                                setFieldValue("price", "");
                                setFieldValue("quantity", "");
                                setFieldValue("stop", "");
                              }}
                              className="priceType"
                            />
                          )}
                        </Field>
                        <ErrorMessage
                          name="selectedPriceOption"
                          component="div"
                          className="error"
                        />
                      </div>
                    </div>
                  </Col>
                  {values?.selectedPriceOption === "Stop Limit" && (
                    <Col
                      lg={
                        values?.selectedPriceOption === "Stop Limit"
                          ? 4
                          : values.selectedPriceOption === "Limit"
                          ? 6
                          : 12
                      }
                      className="my-3"
                    >
                      <div className="units-column">
                        <div className="label-column">
                          <label htmlFor="priceTextInput">Stop Limit</label>
                        </div>
                        <div className="input-column">
                          <Field
                            placeholder="Stop Limit"
                            type="text"
                            id="priceTextInput"
                            name="stop"
                            className="limitTextInput custom-form-control-OrderPreviewField"
                            maxLength={10}
                            style={{ border: "1px solid #ddd" }}
                            autoComplete="off"
                            onPaste={(e) => {
                              e.preventDefault();
                            }}
                            onKeyUp={(event) =>
                              isNumberWithoutDot(event) &&
                              event.preventDefault()
                            }
                            onKeyDown={(evt) =>
                              isNumberWithoutDot(evt) && evt.preventDefault()
                            }
                          />
                        </div>
                        <ErrorMessage
                          name="stop"
                          component="div"
                          className="error"
                        />
                      </div>
                    </Col>
                  )}
                  {values?.selectedPriceOption !== "Market" && (
                    <Col
                      lg={
                        values?.selectedPriceOption === "Stop Limit"
                          ? 4
                          : values.selectedPriceOption === "Limit"
                          ? 6
                          : 12
                      }
                      className="my-3"
                    >
                      <div className="units-column">
                        <div className="label-column">
                          <label htmlFor="unitsTextInput">Order Price</label>
                        </div>
                        <div className="input-column">
                          <div className="units-column greenInput">
                            <div className="combined-input-container d-flex">
                              <input
                                type="text"
                                className="text-input"
                                value={"$"}
                                style={{
                                  backgroundColor: "#ffffff",
                                  border: "1px solid #ddd",
                                  textAlign: "center",
                                  color: "#545454",
                                  fontSize: "19px",
                                  fontFamily: "Jost, sans-serif",
                                }}
                                disabled
                              />
                              <Field
                                placeholder="Price"
                                type="text"
                                id="unitsTextInput"
                                name="price"
                                className="unitTextInput custom-form-control-OrderPreview"
                                maxLength={10}
                                style={{ border: "1px solid #ddd" }}
                                autoComplete="off"
                                onPaste={(e) => {
                                  e.preventDefault();
                                }}
                                onKeyUp={(event) =>
                                  isNumber(event) && event.preventDefault()
                                }
                                onKeyDown={(evt) =>
                                  isNumber(evt) && evt.preventDefault()
                                }
                                onChange={(e) => {
                                  const dotCount = (
                                    e.target.value.toString()?.match(/\./g) ||
                                    []
                                  ).length;
                                  let indexValue =
                                    e.target.value.indexOf(".") == -1
                                      ? e.target.value.length
                                      : e.target.value.indexOf(".");
                                  const validate =
                                    Number(
                                      e.target.value.length - indexValue
                                    ) <= priceDecimalLimit;
                                  if (
                                    !validate &&
                                    dotCount < priceDecimalLimit
                                  ) {
                                    let value = `${Math.trunc(
                                      e.target.value
                                    )}.${e.target.value
                                      ?.split(".")[1]
                                      ?.substring(0, priceDecimalLimit)}`;
                                    return setFieldValue("price", value);
                                  }

                                  if (
                                    String((0.0).toFixed(priceDecimalLimit)) ==
                                    "0." + e.target.value.split(".")[1]
                                  ) {
                                    return setFieldValue(
                                      "price",
                                      Number(e.target.value).toFixed(
                                        priceDecimalLimit
                                      )
                                    );
                                  }

                                  if ("00" == e.target.value) {
                                    return setFieldValue("price", "0");
                                  }

                                  if (
                                    String((0.0).toFixed(priceDecimalLimit)) ==
                                    e.target.value
                                  ) {
                                    return setFieldValue(
                                      "price",
                                      `${(0.0).toFixed(priceDecimalLimit)}`
                                    );
                                  }

                                  if (e.target.value[0] == "." && validate) {
                                    return setFieldValue(
                                      "price",
                                      +"0" + e.target.value
                                    );
                                  }
                                  if (e.target.value === "") {
                                    setFieldValue("price", "");
                                    e.target.value = "";
                                    return;
                                  }
                                  if (
                                    validate &&
                                    dotCount < priceDecimalLimit
                                  ) {
                                    return setFieldValue(
                                      "price",
                                      e.target.value
                                    );
                                  }
                                  if (Number(e.target.value) >= 0 && validate) {
                                    if (e.target.value[-1] == ".") {
                                      return setFieldValue(
                                        "price",
                                        +e.target.value
                                      );
                                    }

                                    setFieldValue(
                                      "price",
                                      truncateToDecimals(
                                        e.target.value,
                                        priceDecimalLimit
                                      )
                                    );
                                  }
                                }}
                              />
                            </div>
                          </div>
                        </div>
                        <ErrorMessage
                          name="price"
                          component="div"
                          className="error"
                        />
                      </div>
                    </Col>
                  )}
                  <Col
                    lg={
                      values?.selectedPriceOption === "Stop Limit"
                        ? 4
                        : values.selectedPriceOption === "Limit"
                        ? 6
                        : 12
                    }
                    className="my-3"
                  >
                    <div className="units-column">
                      <div className="label-column">
                        <label htmlFor="priceTextInput">QTY</label>
                      </div>
                      <div className="input-column">
                        <Field
                          placeholder="Quantity"
                          type="text"
                          id="priceTextInput"
                          name="quantity"
                          className="limitTextInput custom-form-control-OrderPreviewField"
                          maxLength={10}
                          style={{ border: "1px solid #ddd" }}
                          autoComplete="off"
                          onPaste={(e) => {
                            e.preventDefault();
                          }}
                          onKeyUp={(event) =>
                            isNumberWithoutDot(event) && event.preventDefault()
                          }
                          onKeyDown={(evt) =>
                            isNumberWithoutDot(evt) && evt.preventDefault()
                          }
                        />
                      </div>
                      <ErrorMessage
                        name="quantity"
                        component="div"
                        className="error"
                      />
                    </div>
                  </Col>
                </Row>
                {(values.selectedPriceOption === "Stop Limit" ||
                  values.selectedPriceOption === "Limit") && (
                  <Row>
                    <Col lg={4}>
                      <div className="units-column">
                        <div className="label-column">
                          <label htmlFor="textInput">
                            Estimated commission %
                          </label>
                        </div>
                        <div className="input-column">
                          <input
                            type="text"
                            className="estimation-text-input"
                            value={
                              values.selectedOption === "Buy"
                                ? pairFees?.trade_fee_buy + "%"
                                : pairFees?.trade_fee_sell + "%"
                            }
                          />
                        </div>
                      </div>
                    </Col>
                    <Col lg={4}>
                      <div className="units-column">
                        <div className="label-column">
                          <label htmlFor="textInput">
                            Estimated commission
                          </label>
                        </div>
                        <div className="input-column">
                          <input
                            type="text"
                            className="estimation-text-input"
                            value={`$ ${(
                              getTotalValue(
                                values.selectedPriceOption === "Market"
                                  ? Number(pairFees?.last_price)
                                  : Number(values?.price),
                                Number(values?.quantity) *
                                  (values.selectedOption === "Buy"
                                    ? pairFees?.trade_fee_buy
                                    : pairFees?.trade_fee_sell)
                              ) / 100
                            ).toFixed(2)}`}
                          />
                        </div>
                      </div>
                    </Col>
                    <Col lg={4}>
                      <div className="units-column">
                        <div className="label-column">
                          <label htmlFor="textInput">
                            Estimated total cost
                          </label>
                        </div>
                        <div className="input-column">
                          <input
                            type="text"
                            className="estimation-text-input"
                            value={
                              "$" +
                              formatCommaNumbers(
                                Number(
                                  getTotalValue(
                                    Number(
                                      values?.price || pairFees?.last_price || 0
                                    ),
                                    Number(values?.quantity || 0)
                                  ) *
                                    (1 +
                                      (values?.selectedOption === "Buy"
                                        ? Number(pairFees?.trade_fee_buy || 0) /
                                          100
                                        : -Number(
                                            pairFees?.trade_fee_sell || 0
                                          ) / 100))
                                ).toFixed(2)
                              )
                            }
                            disabled
                          />
                        </div>
                      </div>
                    </Col>
                  </Row>
                )}
              </CMBox>

              <CMOffer className="pb-3">
                <Button
                  className="btnSize"
                  type="submit"
                  color="light-green"
                  disabled={
                    pairFees?.last_price === 0 &&
                    values.selectedPriceOption === "Market"
                      ? true
                      : false
                  }
                >
                  Preview order
                </Button>
              </CMOffer>
              {pairFees?.last_price === 0 &&
                values.selectedPriceOption === "Market" && (
                  <div
                    style={{
                      color: "red",
                      fontWeight: "bold",
                      textAlign: "center",
                    }}
                  >
                    You can't place a Market Order because the last price is
                    zero
                  </div>
                )}
            </Form>
          )}
        </Formik>
      </div>
    </>
  );
};

export default OrderPreview;
